import React, { useMemo } from 'react';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import classNames from 'classnames';
import CardTitleBox from '@home/pages/studio/components/CardTitleBox';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import './index.scss';
import high from '@/pages/dashboard/assets/svg/high.svg';
import mid from '@/pages/dashboard/assets/svg/mid.svg';
import low from '@/pages/dashboard/assets/svg/low.svg';

interface IGTDRRiskLevelProps extends BaseComponentProps {
  activeTabs?: string[];
  [key: string]: any;
}

const GTDRRiskLevel = (props: IGTDRRiskLevelProps) => {
  const { style, className, height = 220, data, performanceLoading, columnsRender, formattedData } = props;
  const { riskLevelData } = data || {};
  const { data: riskData, errorMsg, loading } = riskLevelData || {};

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const riskLevelMap = {
    high: {
      name: getName(isEn, 'High risk', '高风险'),
      color: '#FF4D4F',
      icon: high
    },
    mid: {
      name: getName(isEn, 'Medium risk', '中风险'),
      color: '#FFB100',
      icon: mid
    },
    low: {
      name: getName(isEn, 'Low risk', '低风险'),
      color: '#52C41A',
      icon: low
    },
    monitoring: {
      name: getName(isEn, 'Monitoring', '监控中'),
      color: '#52C41A',
      icon: low
    },
  };

  const categoryColorMap = {
    c1: "#1FD2E6",
    c2: "#ED95E0",
    c3: "#395CFF",
    c4: "#F9AD15"
  };

  const columns: any[] = [
    {
      key: 'name',
      header: getName(isEn, 'Dimension', '维度大类'),
      style: { width: '150px', fontWeight: 600 },
      render: (value: { name: string; color: string; }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            className= 'dbd-benchmark-gtdr-risk-level-table-category-cube'
            style={{ backgroundColor: value?.color, }}
          />
          <div>{value?.name}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: getName(isEn, 'Status', '状态'),
      style: { width: '120px', fontWeight: 600 },
      render: (value: string) => (
        <div
          className={classNames(
            'dbd-benchmark-gtdr-risk-level-table-status',
            value,
          )}
        >
          <div
            className={classNames(isEn ? 'dbd-benchmark-gtdr-risk-level-table-isen' : '')}
            style={{ display: "flex", alignItems: "center" }}
          >
            <img className="dbd-benchmark-gtdr-risk-level-table-status-icon" src={riskLevelMap[value]?.icon}></img>
            <span className="dbd-benchmark-gtdr-risk-level-table-status-text"> {riskLevelMap[value]?.name || value}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'risk',
      header: getName(isEn, 'Risk', '风险'),
      style: { minWidth: '350px', fontWeight: 600 },
      render: (value: string) => (
        <div dangerouslySetInnerHTML={{ __html: value || '-' }}></div>
      ),
    },
    {
      key: 'plan',
      header: getName(isEn, 'Plan', '方案'),
      style: { minWidth: '350px', fontWeight: 600 },
      render: (value: string) => (
        <div dangerouslySetInnerHTML={{ __html: value || '-' }}></div>
      ),
    },
  ];

  const newData = useMemo(() => {
    const newData = riskData?.map((item: any) => {
      const {
        category_cn,
        category_en,
        risk_cn,
        risk_en,
        solution_cn,
        solution_en,
        risk_level,
        category
      } = item;

      return {
        name: {name: getName(isEn, category_en, category_cn),color: categoryColorMap[category]},
        status: risk_level,
        risk: getName(isEn, risk_en, risk_cn),
        plan: getName(isEn, solution_en, solution_cn),
      };
    });
    return newData;
  }, [isEn, data]);

  return (
    <CardTitleBox
      title={''}
      className={classNames(
        'dbd-benchmark-gtdr-risk-level-card-container',
        className,
      )}
      style={style}
      normalConfig={{
        isLoading: loading || performanceLoading,
        isError: !!errorMsg,
        isNoData: riskLevelData?.length === 0,
        style: { height },
      }}
    >
      <table className="dbd-benchmark-gtdr-risk-level-table">
        <thead>
          <tr>
            {(columnsRender || columns)?.map((item) => (
              <th key={item.key} style={item.style}>
                {item.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(formattedData || newData)?.map((item: any) => {
            const { category } = item;
            return (
              <tr key={category}>
                {(columnsRender || columns)?.map((col) => {
                  const { key, render } = col;
                  return (
                    <td key={key}>
                      {render ? render(item[col.key], item) : item[col.key]}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </CardTitleBox>
  );
};

export default GTDRRiskLevel;
